import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';

import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { applySmartBounds, isPointWithinBounds, mapOptions } from 'src/utils/functions';
import {
  Bounds,
  Coordinates,
  FullMarkersFetchTriggerPoint,
  Listing,
  ListingVal,
  Submarket,
} from 'src/interfaces/idashboard';
import {
  COLOR_PRIMARY_DARK,
  exchangeRates,
  HEATMAP_METRIC_TO_DATA_KEY_MAP,
  POINTER_GREEN,
} from 'src/const';
import {
  bfsMergeHexagons,
  calculatePolygonCentroid,
  establishSpatialRelationships,
  generateHexGrid,
  getMedianOccupancyRate,
  getMedianRevenue,
  getNaturalBreaks,
  getPolygonPaths,
  getScale,
  getSubmarketColor,
  recursiveGenerateNeighbourhoods,
} from '../helper';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';
import { callApi } from 'src/core/callApi';
import { COLOR_PRIMARY_2, NEIGHBOURHOOD_THEME } from 'src/colors';
import { ZoomButtons } from '../UtilityComponents/ZoomButtons';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { InfoButton } from 'src/App/Components/Buttons';
import InfoTooltipContent from 'src/App/HOC/Tooltips/InfoTooltipContent';
import HeatmapPointer from 'src/App/HOC/Pointer/HeatmapPointer';
import ShowMoreButton from 'src/App/Components/Buttons/ShowMoreButton';
import HeatmapLegendBox from '../UtilityComponents/HeatmapLegendBox';

const MAP_FILL_COLOR = POINTER_GREEN;
const classifyPoint = require('robust-point-in-polygon');

const getLegendUnit = (neighbourhoodAnalysisKey, currency) => {
  if (neighbourhoodAnalysisKey == 'revenue') {
    return [exchangeRates[currency].symbol, 'left'];
  } else if (neighbourhoodAnalysisKey == 'occupancy') {
    return ['%', 'right'];
  } else return ['', 'right'];
};

const formatLegendLabel = (min, max, unit, location) => {
  if (location === 'left') {
    return max !== undefined ? `${unit} ${min} - ${unit} ${max}` : `${unit} ${min}`;
  } else {
    return max !== undefined ? `${min}${unit} - ${max}${unit}` : `${min}${unit}`;
  }
};

const tooltipConfig = {
  revenue: 'Identify high-demand neighborhoods and areas with strong revenue potential.',
  occupancy:
    'Analyze occupancy trends across different submarkets to spot areas with high or low demand.',
  listing:
    'Gauge market saturation and competition by examining the number of active listings in each submarket.',
};

const ConfigSelect = ({
  value,
  setValue,
  optionsMap,
  customStyle,
  activeColor,
  isDisabled,
  useFullMarkers,
  setShowConfirmFullMarkersFetchModal,
  setFullMarkersFetchTriggerPoint,
  shouldShowVerifiedSubmarkets,
}: {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<any>>;
  optionsMap: { [index: string]: string };
  customStyle?: any;
  activeColor: string;
  isDisabled: boolean;
  useFullMarkers: boolean;
  setShowConfirmFullMarkersFetchModal: React.Dispatch<React.SetStateAction<boolean>>;
  setFullMarkersFetchTriggerPoint: React.Dispatch<
    React.SetStateAction<FullMarkersFetchTriggerPoint | null>
  >;
  shouldShowVerifiedSubmarkets: boolean;
}) => (
  <FormControl variant="standard" sx={{ m: 0, minWidth: 120 }}>
    <Select
      value={value}
      onChange={event => {
        logAmplitudeEvent('heatmap_configured');
        if (
          !isDisabled &&
          !useFullMarkers &&
          event.target.value !== 'listing' &&
          !shouldShowVerifiedSubmarkets
        ) {
          setShowConfirmFullMarkersFetchModal(true);
          setFullMarkersFetchTriggerPoint('heatmap');
          return;
        }
        setValue(event.target.value);
      }}
      MenuProps={{
        autoFocus: false,
      }}
      sx={{
        fontWeight: 500,
        paddingTop: 0,
        '& #demo-simple-select-standard': {
          paddingTop: '0px !important',
          paddingLeft: '5px',
        },
        '& .MuiSelect-standard': {
          paddingTop: '0px !important',
          backgroundColor: 'transparent',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
          outline: 'none',
        },
        fieldset: {
          border: 'none !important',
          outline: 'none',
        },
        ...customStyle,
      }}
      renderValue={selected => (
        <Typography variant="body1" sx={{ fontWeight: 500, color: activeColor }}>
          {optionsMap[selected]}
        </Typography>
      )}
    >
      {Object.keys(optionsMap).map(option => (
        <MenuItem value={option}>{optionsMap[option]}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const getSmartBoundedSubmarkets = (submarkets: Submarket[], bounds: GoogleMapReact.Bounds) => {
  // Filter submarkets whose centroid falls within the bounds
  const boundedSubmarkets = submarkets.filter(submarket => {
    const centroid = calculatePolygonCentroid(submarket.path[0]);
    const centroidString = `POINT(${centroid.lng} ${centroid.lat})`;
    return isPointWithinBounds(centroidString, bounds);
  });

  // Group submarkets by color
  const submarketsByColor = boundedSubmarkets.reduce((acc, submarket) => {
    const color = submarket.color;
    if (!acc[color]) {
      acc[color] = [];
    }
    acc[color].push(submarket);
    return acc;
  }, {} as Record<string, Submarket[]>);

  const selectedSubmarkets: Submarket[] = [];

  // Try to select one submarket from each color group if possible. Prioritize the highest listing count
  Object.values(submarketsByColor).forEach(submarketGroup => {
    if (selectedSubmarkets.length < 3) {
      const highestListingCountSubmarket = submarketGroup.sort(
        (a, b) => b.listingCount - a.listingCount
      )[0];
      selectedSubmarkets.push(highestListingCountSubmarket);
    }
  });

  // If we don't have enough submarkets with different colors, select additional ones from the remaining list. Prioritize the highest listing count
  if (selectedSubmarkets.length < 3) {
    const remainingSubmarkets = boundedSubmarkets
      .filter(submarket => !selectedSubmarkets.includes(submarket))
      .sort((a, b) => b.listingCount - a.listingCount);
    selectedSubmarkets.push(...remainingSubmarkets.slice(0, 3 - selectedSubmarkets.length));
  }

  return selectedSubmarkets;
};

const PerformanceHeatmap = ({
  listings,
  currency,
  isLoggedIn,
  handleOnSampleClick,
  matches,
  isDisabled,
  setShowConfirmFullMarkersFetchModal,
  setFullMarkersFetchTriggerPoint,
  verifiedSubmarkets,
  marketTotalSupply,
  marketDisplayName,
  parentMarketDetails,
  setDashboardLayoutView,
}) => {
  const dispatch = useDispatch();
  const search = useSelector(state => (state as any).dashboard.search);
  const isUserAllowed = useSelector(state => (state as any).dashboard.isUserAllowed);
  const multipleMarkersCallLoading = useSelector(
    state => (state as any).dashboard.multipleMarkersCallLoading
  );
  const user = useSelector(state => (state as any).dashboard.user);
  const useFullMarkers = useSelector(state => (state as any).dashboard.useFullMarkers);

  const mapCenter = {
    lat: search.data.lat,
    lng: search.data.lng,
  };

  const [mapCopy, setMapCopy] = useState<any>(null);
  const [mapsCopy, setMapsCopy] = useState<any>(null);
  const [zoom, setZoom] = useState(12);
  const NEIGHBOURHOOD_IDEAL_sAMPLE_SIZE = 50;
  const [submarketObjs, setSubmarketObjs] = useState<Submarket[]>([]);
  const [boundedSubmarketObjs, setBoundedSubmarketObjs] = useState<Submarket[] | any[]>([]);
  const submarketPolygonSetRef = useRef(false);
  const [submarketPolygonReferences, setSubmarketPolygonReferences] = useState<any>([]);
  const [neighbourhoodChoroplethLegends, setNeighbourhoodChoroplethLegends] = useState<any>({});
  const [neighbourhoodAnalysisKey, setNeighbourhoodAnalysisKey] = useState<string>('listing');
  const [submarketCachedata, setsubmarketCachedata] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [shouldShowVerifiedSubmarkets, setShouldShowVerifiedSubmarkets] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [hoveredSubmarketData, setHoveredSubmarketData] = useState<any>(null);

  const upgradeMessage = useUpgradeMesageProFeature();

  useEffect(() => {
    if (
      verifiedSubmarkets != 'loading' &&
      verifiedSubmarkets &&
      verifiedSubmarkets.length > 0 &&
      marketTotalSupply
    ) {
      const totalSubmarketsSupply = verifiedSubmarkets.reduce(
        (acc, submarket) => acc + submarket.supply,
        0
      );

      if (totalSubmarketsSupply / marketTotalSupply > 0.6) {
        setShouldShowVerifiedSubmarkets(true);
      }
    }
  }, [verifiedSubmarkets, marketTotalSupply]);

  useEffect(() => {
    if (isDisabled) {
      setNeighbourhoodAnalysisKey('revenue');
    } else if (useFullMarkers) {
      setNeighbourhoodAnalysisKey('revenue');
    } else {
      setNeighbourhoodAnalysisKey('listing');
    }
  }, [useFullMarkers, isDisabled]);

  // this is to fetch cached data for submarket analysis
  useEffect(() => {
    const fetchData = async () => {
      try {
        const URL =
          'https://kqypxjqy92.execute-api.us-east-2.amazonaws.com/default/getMarketListingsCoordinates';
        const res = await callApi(
          URL,
          {
            queryParams: search.data.isSubmarket
              ? { submarketID: search.data.id }
              : {
                  name: search.data.name,
                  city: search.data.city,
                  countryCode: search.data.countryCode,
                },
          },
          false
        );
        setsubmarketCachedata(res.message);
      } catch (error) {
        console.error('Error fetching submarket cache data:', error);
      }
    };
    if (search.data.name || search.data.city || search.data.countryCode) {
      fetchData();
    }
  }, [search]);

  const handleMapChange = async ({ center, bounds, zoom }: GoogleMapReact.ChangeEventValue) => {
    setZoom(zoom);

    const boundedSubmarkets = getSmartBoundedSubmarkets(submarketObjs, bounds);
    setBoundedSubmarketObjs(boundedSubmarkets);
  };

  const setPolygon = (
    map,
    maps,
    paths: Coordinates[][],
    strokeColor: string,
    fillColor: string,
    strokeOpacity = 0.8,
    strokeWeight = 2,
    fillOpacity = 0.12,
    submarketId?: string,
    submarketData?: any
  ) => {
    const polygon = new maps.Polygon({
      paths: paths,
      strokeColor: strokeColor,
      strokeOpacity: strokeOpacity,
      strokeWeight: strokeWeight,
      fillColor: fillColor,
      fillOpacity: fillOpacity,
    });
    polygon.setMap(map);

    if (submarketId) {
      maps.event.addListener(polygon, 'mouseover', e => {
        polygon.setOptions({
          fillOpacity: 0.4,
          strokeWeight: 3,
        });
        setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        setHoveredSubmarketData(submarketData);
      });

      maps.event.addListener(polygon, 'mousemove', e => {
        setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });

      maps.event.addListener(polygon, 'mouseout', () => {
        polygon.setOptions({
          fillOpacity: fillOpacity,
          strokeWeight: strokeWeight,
        });
        setTooltipPosition(null);
        setHoveredSubmarketData(null);
      });
    }

    return polygon;
  };

  const apiIsLoaded = (map, maps, polygonRaw: string, multiPolygonRaw: string) => {
    const paths = getPolygonPaths(polygonRaw, multiPolygonRaw);
    const polygon = setPolygon(map, maps, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
    applySmartBounds(map, maps, {} as Bounds, [polygon]);
    setMapCopy(map);
    setMapsCopy(maps);
  };

  const neighbourhoodMarkers = useMemo(() => {
    return boundedSubmarketObjs.map(submarketObj => {
      const centroid = calculatePolygonCentroid(submarketObj.path[0]);
      const scale = getScale(zoom);
      return (
        <HeatmapPointer
          lat={centroid.lat}
          lng={centroid.lng}
          pointerText={submarketObj?.submarketName ?? null}
          metric={HEATMAP_METRIC_TO_DATA_KEY_MAP[neighbourhoodAnalysisKey].metric}
          metricValue={
            submarketObj[HEATMAP_METRIC_TO_DATA_KEY_MAP[neighbourhoodAnalysisKey].dataKey]
          }
          currency={currency}
          userhasDataAccess={isUserAllowed}
          handleOnSampleClick={handleOnSampleClick}
          scale={scale}
        />
      );
    });
  }, [
    boundedSubmarketObjs,
    neighbourhoodAnalysisKey,
    isUserAllowed,
    currency,
    multipleMarkersCallLoading,
    user,
    isLoggedIn,
    zoom,
  ]);

  useEffect(() => {
    if (verifiedSubmarkets == 'loading') {
      return;
    }

    if (shouldShowVerifiedSubmarkets) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // check if submarket polygons are already set remove it and create a new polygon
    if (submarketPolygonSetRef.current) {
      submarketPolygonReferences.forEach(submarketPolygon => submarketPolygon.setMap(null));
      submarketPolygonSetRef.current = false;
    }

    if (
      !mapCopy ||
      !mapsCopy ||
      search.loading ||
      (!search.data.polygon && !search.data.multiPolygon) ||
      !submarketCachedata ||
      (useFullMarkers && !listings.data)
    ) {
      setNeighbourhoodChoroplethLegends({});
      setBoundedSubmarketObjs([]);
      return;
    }

    // explode the market map in hexagonal grids
    let hexGrid;
    if (!useFullMarkers) {
      hexGrid = generateHexGrid(search.data.polygon, search.data.multiPolygon, submarketCachedata);
    } else {
      hexGrid = generateHexGrid(search.data.polygon, search.data.multiPolygon, listings.data);
    }

    // transform the hexagonal grid into neighbourhoods by setting listings count metadata
    const neighbourhoods = hexGrid.map(hexagon => {
      let listingsInHexagon: ListingVal[] = [];
      if (!hexagon)
        return {
          coordinates: [],
          listings: [],
        };

      // Check if all listings are loaded then show actual data else show cache
      if (!listings.data) {
        submarketCachedata.forEach(coord => {
          if (classifyPoint(hexagon[0], coord) === -1 || classifyPoint(hexagon[0], coord) === 0) {
            listingsInHexagon.push(coord);
          }
        });
      } else {
        Object.values(listings.data as Listing).forEach(listing => {
          const point = [listing.longitude, listing.latitude];
          if (classifyPoint(hexagon[0], point) === -1 || classifyPoint(hexagon[0], point) === 0)
            listingsInHexagon.push(listing);
        });
      }

      return {
        coordinates: hexagon[0],
        listings: listingsInHexagon,
      };
    });

    const filteredNeighbourhoods = neighbourhoods.filter(
      neighbourhood => neighbourhood.listings.length > 0
    );

    // merge the neighbourhoods untill each neighbourhood has minimum half of the ideal listings count
    const neighbourhoodMergingMinThreshold = NEIGHBOURHOOD_IDEAL_sAMPLE_SIZE / 2;
    let spatialRelationships = establishSpatialRelationships(filteredNeighbourhoods);
    let mergedNeighbourhoods: any = filteredNeighbourhoods;
    let isFirstIteration = true;

    while (
      isFirstIteration ||
      mergedNeighbourhoods.some(
        (neighbourhood, index) =>
          neighbourhood.listings.length < neighbourhoodMergingMinThreshold &&
          spatialRelationships[index].length > 0
      )
    ) {
      mergedNeighbourhoods = bfsMergeHexagons(
        mergedNeighbourhoods,
        spatialRelationships,
        neighbourhoodMergingMinThreshold
      );
      spatialRelationships = establishSpatialRelationships(mergedNeighbourhoods);
      isFirstIteration = false;
    }

    // submarkets are ready
    const submarkets: Submarket[] = mergedNeighbourhoods.map((neighbourhood, index) => {
      let listingCount = neighbourhood.listings.length;
      const hexagonPath = neighbourhood.coordinates.map(coord => ({
        lng: coord[0],
        lat: coord[1],
      }));
      const medianOccupancyRate = getMedianOccupancyRate(neighbourhood.listings);
      const medianRevenue = getMedianRevenue(neighbourhood.listings);
      return {
        ...neighbourhood,
        path: [hexagonPath],
        occupancyRate: medianOccupancyRate,
        id: index,
        listingCount: listingCount,
        revenue: medianRevenue,
      };
    });
    // set vhropleth graph breaks and assign colors to submarkets
    const submarketOccupancyRates = submarkets.map(submarket => submarket.occupancyRate);
    const validSubmarketOccupancyRates = submarketOccupancyRates.filter(
      rate => rate !== undefined && !Number.isNaN(rate)
    );

    const submarketRevenues = submarkets.map(submarket => submarket.revenue);
    const validSubmarketRevenues = submarketRevenues.filter(
      rate => rate !== undefined && !Number.isNaN(rate)
    );

    const submarketListingCounts = submarkets.map(submarket => submarket.listingCount);
    const validSubmarketListingsCounts = submarketListingCounts.filter(
      count => count !== undefined && !Number.isNaN(count)
    );

    let choroplethBreaks;
    if (neighbourhoodAnalysisKey === 'occupancy') {
      choroplethBreaks = getNaturalBreaks(validSubmarketOccupancyRates);
    }
    if (neighbourhoodAnalysisKey === 'listing') {
      choroplethBreaks = getNaturalBreaks(validSubmarketListingsCounts);
    }
    if (neighbourhoodAnalysisKey === 'revenue') {
      choroplethBreaks = getNaturalBreaks(validSubmarketRevenues);
    }

    const [unit, location] = getLegendUnit(neighbourhoodAnalysisKey, currency);

    const legends = {
      [`< ${formatLegendLabel(
        choroplethBreaks[0] + 1,
        undefined,
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[0],
      [`${formatLegendLabel(
        choroplethBreaks[0] + 1,
        choroplethBreaks[1],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[1],
      [`${formatLegendLabel(
        choroplethBreaks[1] + 1,
        choroplethBreaks[2],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[2],
      [`${formatLegendLabel(
        choroplethBreaks[2] + 1,
        choroplethBreaks[3],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[3],
      [`> ${formatLegendLabel(
        choroplethBreaks[3],
        undefined,
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[4],
    };
    setNeighbourhoodChoroplethLegends(legends);

    let submarketPolygons: any[] = [];

    submarkets.forEach(submarket => {
      const submarketKey =
        neighbourhoodAnalysisKey === 'occupancy'
          ? submarket.occupancyRate
          : neighbourhoodAnalysisKey === 'revenue'
          ? submarket.revenue
          : submarket.listingCount;
      const color = getSubmarketColor(submarketKey, choroplethBreaks, neighbourhoodAnalysisKey);

      submarket.color = color;

      const submarketPolygon = setPolygon(
        mapCopy,
        mapsCopy,
        submarket.path,
        COLOR_PRIMARY_DARK,
        color,
        0.8,
        2,
        0.8
      );
      submarketPolygons.push(submarketPolygon);
    });

    setSubmarketPolygonReferences(submarketPolygons);
    setSubmarketObjs(submarkets);
    submarketPolygonSetRef.current = true;
    setLoading(false);
  }, [
    neighbourhoodAnalysisKey,
    multipleMarkersCallLoading,
    mapCopy,
    mapsCopy,
    search,
    submarketCachedata,
    listings.data,
    useFullMarkers,
    shouldShowVerifiedSubmarkets,
    verifiedSubmarkets,
  ]);

  const getCoordinatesFromWKT = (wktBoundaryString: string) => {
    const coordinates = wktBoundaryString
      .replace('POLYGON ((', '')
      .replace('))', '')
      .split(',')
      .map(coord =>
        coord
          .trim()
          .split(' ')
          .map(Number)
      );
    return coordinates;
  };

  useEffect(() => {
    if (shouldShowVerifiedSubmarkets && mapCopy && mapsCopy) {
      setLoading(true);
      const submarkets = verifiedSubmarkets.map((neighbourhood, index) => {
        const wktBoundaryString = neighbourhood.boundary;
        const neighbourhoodCoordinates = getCoordinatesFromWKT(wktBoundaryString);
        const hexagonPath = neighbourhoodCoordinates.map(coord => ({
          lng: coord[0],
          lat: coord[1],
        }));
        return {
          ...neighbourhood,
          path: [hexagonPath],
          occupancyRate: neighbourhood.occupancy,
          id: index,
          listingCount: neighbourhood.supply,
          revenue: neighbourhood.revenue,
        };
      });
      // set vhropleth graph breaks and assign colors to submarkets
      const submarketOccupancyRates = submarkets.map(submarket => submarket.occupancyRate);
      const validSubmarketOccupancyRates = isUserAllowed
        ? submarketOccupancyRates.filter(rate => rate !== undefined && !Number.isNaN(rate))
        : [];

      const submarketRevenues = submarkets.map(submarket => submarket.revenue);
      const validSubmarketRevenues = submarketRevenues.filter(
        rate => rate !== undefined && !Number.isNaN(rate)
      );

      const submarketListingCounts = submarkets.map(submarket => submarket.listingCount);
      const validSubmarketListingsCounts = submarketListingCounts.filter(
        count => count !== undefined && !Number.isNaN(count)
      );

      let choroplethBreaks;
      if (neighbourhoodAnalysisKey === 'occupancy') {
        choroplethBreaks = getNaturalBreaks(validSubmarketOccupancyRates);
      }
      if (neighbourhoodAnalysisKey === 'listing') {
        choroplethBreaks = getNaturalBreaks(validSubmarketListingsCounts);
      }
      if (neighbourhoodAnalysisKey === 'revenue') {
        choroplethBreaks = getNaturalBreaks(validSubmarketRevenues);
      }

      const [unit, location] = getLegendUnit(neighbourhoodAnalysisKey, currency);

      const legends = {
        [`< ${formatLegendLabel(
          choroplethBreaks[0] + 1,
          undefined,
          unit,
          location
        )}`]: NEIGHBOURHOOD_THEME[0],
        [`${formatLegendLabel(
          choroplethBreaks[0] + 1,
          choroplethBreaks[1],
          unit,
          location
        )}`]: NEIGHBOURHOOD_THEME[1],
        [`${formatLegendLabel(
          choroplethBreaks[1] + 1,
          choroplethBreaks[2],
          unit,
          location
        )}`]: NEIGHBOURHOOD_THEME[2],
        [`${formatLegendLabel(
          choroplethBreaks[2] + 1,
          choroplethBreaks[3],
          unit,
          location
        )}`]: NEIGHBOURHOOD_THEME[3],
        [`> ${formatLegendLabel(
          choroplethBreaks[3],
          undefined,
          unit,
          location
        )}`]: NEIGHBOURHOOD_THEME[4],
      };
      setNeighbourhoodChoroplethLegends(legends);

      let submarketPolygons: any[] = [];

      submarkets.forEach(submarket => {
        const submarketKey =
          neighbourhoodAnalysisKey === 'occupancy'
            ? submarket.occupancyRate
            : neighbourhoodAnalysisKey === 'revenue'
            ? submarket.revenue
            : submarket.listingCount;
        const color = getSubmarketColor(submarketKey, choroplethBreaks, neighbourhoodAnalysisKey);

        submarket.color = color;

        const submarketPolygon = setPolygon(
          mapCopy,
          mapsCopy,
          submarket.path,
          COLOR_PRIMARY_DARK,
          color,
          0.8,
          2,
          0.8,
          submarket.submarketId,
          submarket
        );
        submarketPolygons.push(submarketPolygon);
      });

      setSubmarketPolygonReferences(submarketPolygons);
      setSubmarketObjs(submarkets);
      submarketPolygonSetRef.current = true;
      setLoading(false);
    }
  }, [
    shouldShowVerifiedSubmarkets,
    verifiedSubmarkets,
    mapCopy,
    mapsCopy,
    neighbourhoodAnalysisKey,
    currency,
  ]);

  const hideData = isDisabled && neighbourhoodAnalysisKey !== 'listing';

  const getHeatmapTitle = () => {
    if (!parentMarketDetails)
      return <Typography sx={{ ml: 1, fontWeight: 'bold' }}>{marketDisplayName}</Typography>;

    const country = parentMarketDetails.country
      ? parentMarketDetails.country.replace(/\s/g, '-').toLocaleLowerCase()
      : '0';
    const city = parentMarketDetails.city ? parentMarketDetails.city.toLocaleLowerCase() : '0';
    const district = parentMarketDetails.district.toLocaleLowerCase();

    const parentMarketUrl = `/airbnb-data/${country}/${city}/${district}`;

    return (
      <Link underline="hover" color="primary" href={parentMarketUrl} target="_blank" sx={{ ml: 1 }}>
        {parentMarketDetails.displayName
          ? parentMarketDetails.displayName
          : parentMarketDetails.district}
      </Link>
    );
  };

  return (
    <Grid
      item
      xs={12}
      sx={{
        width: '100%',
        padding: '20px',
        boxShadow: '0 4px 10px #0003',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', mb: 1 }}>
        <Typography
          variant="body1"
          className="subHeadings"
          sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
        >
          <ConfigSelect
            value={neighbourhoodAnalysisKey}
            setValue={setNeighbourhoodAnalysisKey}
            optionsMap={{
              revenue: 'Revenue',
              occupancy: 'Occupancy Rate',
              listing: 'Supply',
            }}
            activeColor={COLOR_PRIMARY_2}
            isDisabled={isDisabled}
            useFullMarkers={useFullMarkers}
            setShowConfirmFullMarkersFetchModal={setShowConfirmFullMarkersFetchModal}
            setFullMarkersFetchTriggerPoint={setFullMarkersFetchTriggerPoint}
            shouldShowVerifiedSubmarkets={shouldShowVerifiedSubmarkets}
          />
          by Submarkets in {getHeatmapTitle()} <Box sx={{ ml: 1 }} />
          <InfoButton
            tooltipContent={
              <InfoTooltipContent content={tooltipConfig[neighbourhoodAnalysisKey]} />
            }
          />
        </Typography>

        {loading && <CircularProgress size={20} />}
      </Box>

      <Paper sx={{ height: '400px', width: '100%', position: 'relative', top: 0, left: 0 }}>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
            libraries: ['drawing', 'places', 'visualization'],
            id: 'dashboard2',
          }}
          center={mapCenter}
          options={map => ({
            ...mapOptions(map, false),
            scrollwheel: false,
          })}
          zoom={zoom}
          resetBoundsOnResize={true}
          onChange={handleMapChange}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) =>
            apiIsLoaded(map, maps, search.data.polygon, search.data.multiPolygon)
          }
        >
          {/* dynamically show neighburhood chips to avoid conjestion in dashboard view */}
          {!hideData && neighbourhoodMarkers}
          {!hideData && tooltipPosition && hoveredSubmarketData && (
            <HeatmapPointer
              lat={tooltipPosition.lat}
              lng={tooltipPosition.lng}
              pointerText={hoveredSubmarketData?.submarketName ?? null}
              metric={HEATMAP_METRIC_TO_DATA_KEY_MAP[neighbourhoodAnalysisKey].metric}
              metricValue={
                hoveredSubmarketData[
                  HEATMAP_METRIC_TO_DATA_KEY_MAP[neighbourhoodAnalysisKey].dataKey
                ]
              }
              currency={currency}
              userhasDataAccess={isUserAllowed}
              handleOnSampleClick={handleOnSampleClick}
              scale={1}
            />
          )}
        </GoogleMapReact>

        <Grid
          item
          xs={12}
          justifyContent={'flex-end'}
          sx={{
            position: 'absolute',
            bottom: '0px',
            right: '0px',
          }}
        >
          <ZoomButtons size={50} setZoom={setZoom} zoomInAction={() => {}} />
        </Grid>

        {!matches && (
          <HeatmapLegendBox
            hideData={hideData}
            neighbourhoodAnalysisKey={neighbourhoodAnalysisKey}
            neighbourhoodChoroplethLegends={neighbourhoodChoroplethLegends}
            loading={loading}
          />
        )}

        {hideData && (
          <Typography
            variant="body1"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              fontSize: '20px',
              fontWeight: 500,
              color: COLOR_PRIMARY_2,
              cursor: 'pointer',
              pointerEvents: 'auto',
              textDecoration: 'underline',
              background: '#fff5',
              backdropFilter: 'blur(5px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={() => {
              handleOnSampleClick('expert');
            }}
          >
            {upgradeMessage}
          </Typography>
        )}
      </Paper>

      {shouldShowVerifiedSubmarkets && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'row-reverse',
            marginTop: '10px',
          }}
        >
          <ShowMoreButton
            isShowingLess={true}
            isDisabled={false}
            handleButtonClick={() => {
              setDashboardLayoutView('submarkets');
            }}
            ctaText="Explore Heatmap"
          />
        </Box>
      )}

      {matches && (
        <Grid item sx={{ mt: 2 }}>
          <HeatmapLegendBox
            hideData={hideData}
            neighbourhoodAnalysisKey={neighbourhoodAnalysisKey}
            neighbourhoodChoroplethLegends={neighbourhoodChoroplethLegends}
            loading={loading}
            defaultExpanded={false}
            additionalStyle={{
              position: 'relative',
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default React.memo(PerformanceHeatmap);
